import { useState, useEffect } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Trash2, KeyRound, Eye, EyeOff } from 'lucide-react';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  created_at: string;
  last_login: string | null;
  status: 'enabled' | 'disabled';
  role?: string;
}

export function UserManagement() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [deleteProfile, setDeleteProfile] = useState<Profile | null>(null);
  const [resetPasswordProfile, setResetPasswordProfile] = useState<Profile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', username: '', email: '', status: 'enabled' as 'enabled' | 'disabled' });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      
      // Fetch profiles with their roles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch user roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Merge profiles with roles
      const profilesWithRoles = profilesData?.map(profile => {
        const userRole = rolesData?.find(r => r.user_id === profile.id);
        return {
          ...profile,
          role: userRole?.role || 'user'
        };
      });

      setProfiles(profilesWithRoles || []);
    } catch (error) {
      toast.error('Failed to load users: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile: Profile) => {
    setEditingProfile(profile);
    setEditForm({
      full_name: profile.full_name || '',
      username: profile.username || '',
      email: profile.email,
      status: profile.status,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingProfile) return;
    
    try {
      // Prevent disabling admin accounts
      if (editingProfile.role === 'admin' && editForm.status === 'disabled') {
        toast.error('Admin account tidak dapat dinonaktifkan');
        return;
      }

      console.log('Saving edit with status:', editForm.status, 'for user:', editingProfile.id);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.full_name,
          username: editForm.username || null,
          email: editForm.email,
          status: editForm.status,
        })
        .eq('id', editingProfile.id);

      if (error) {
        console.error('Update error:', error);
        if (error.code === '23505' && error.message.includes('profiles_username_key')) {
          toast.error('Username tersebut sudah digunakan.');
          return;
        }
        throw error;
      }

      console.log('Update successful');
      
      toast.success('User updated successfully');
      
      setEditingProfile(null);
      
      // Force refresh from database to ensure we have the latest data
      await fetchProfiles();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user: ' + getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteProfile) return;

    try {
      const { error } = await supabase.functions.invoke('delete-user', {
        body: {
          userId: deleteProfile.id
        }
      });

      if (error) throw error;

      toast.success('User deleted successfully');
      setDeleteProfile(null);
      fetchProfiles();
    } catch (error) {
      toast.error('Failed to delete user: ' + getErrorMessage(error));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordProfile || !newPassword) {
      toast.error('Silakan masukkan password baru');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      toast.error('Password harus mengandung minimal 1 angka');
      return;
    }

    if (!/[a-zA-Z]/.test(newPassword)) {
      toast.error('Password harus mengandung minimal 1 huruf');
      return;
    }

    try {
      const response = await supabase.functions.invoke('reset-user-password', {
        body: {
          userId: resetPasswordProfile.id,
          newPassword: newPassword
        }
      });

      console.log('Reset password response:', response);

      // Check if there's an error in the response
      if (response.error) {
        console.error('Edge function error:', response.error);
        
        // The error message is in response.error.message which contains the status text
        // We need to show a generic message or extract from context
        let errorMessage = 'Gagal mereset password';
        
        // Check if the error is related to authentication
        if (response.error.message && response.error.message.includes('Authentication')) {
          errorMessage = 'Sesi Anda telah berakhir. Silakan login ulang.';
        }
        
        toast.error(errorMessage);
        return;
      }

      // Check the response data for error field
      if (response.data) {
        // If data contains an error field, it means the edge function returned an error response
        if (response.data.error) {
          console.log('Error from edge function:', response.data.error);
          toast.error(response.data.error);
          return;
        }
        
        // If data contains success field, show success message
        if (response.data.success) {
          toast.success('Password berhasil direset');
          setResetPasswordProfile(null);
          setNewPassword('');
          setShowPassword(false);
          return;
        }
      }

      // Default success if no error
      toast.success('Password berhasil direset');
      setResetPasswordProfile(null);
      setNewPassword('');
      setShowPassword(false);
      
    } catch (error) {
      console.error('Reset password catch error:', error);
      toast.error('Gagal mereset password. Silakan coba lagi.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">User Management</CardTitle>
          <CardDescription>View and manage all registered users</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          <div className="px-6">
            <Table>
              <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Full Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell className="font-medium">{profile.email}</TableCell>
                  <TableCell>{profile.full_name || '-'}</TableCell>
                  <TableCell>
                    {profile.username ? (
                      <Badge variant="secondary">@{profile.username}</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={profile.status === 'enabled' ? 'default' : 'destructive'}
                    >
                      {profile.status === 'enabled' ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={profile.role === 'admin' ? 'default' : 'outline'}
                      className={profile.role === 'admin' ? 'bg-primary' : ''}
                    >
                      {profile.role === 'admin' ? 'Administrator' : 'User'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(profile.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {profile.last_login ? (
                      <span className="text-sm">
                        {new Date(profile.last_login).toLocaleString('id-ID', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(profile)}
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setResetPasswordProfile(profile)}
                        title="Reset password"
                      >
                        <KeyRound className="h-4 w-4 text-orange-500" />
                      </Button>
                      {profile.role !== 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteProfile(profile)}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingProfile} onOpenChange={(open) => !open && setEditingProfile(null)}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-username">Username</Label>
              <Input
                id="edit-username"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Account Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value: 'enabled' | 'disabled') => setEditForm({ ...editForm, status: value })}
                disabled={editingProfile?.role === 'admin'}
              >
                <SelectTrigger id="edit-status">
                  <SelectValue placeholder="Pilih status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">
                    <span className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Enabled</Badge>
                      <span className="text-xs text-muted-foreground">User can login</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="disabled">
                    <span className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">Disabled</Badge>
                      <span className="text-xs text-muted-foreground">User cannot login</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {editingProfile?.role === 'admin' ? (
                <p className="text-xs text-muted-foreground">
                  🛡️ Admin account tidak dapat dinonaktifkan
                </p>
              ) : editForm.status === 'disabled' ? (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠️ User tidak akan bisa login jika status disabled
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProfile(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordProfile} onOpenChange={(open) => {
        if (!open) {
          setResetPasswordProfile(null);
          setNewPassword('');
          setShowPassword(false);
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordProfile?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-2">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                ⚠️ <strong>Penting:</strong> Password baru harus berbeda dari password lama user dan harus kombinasi huruf & angka
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Masukkan password baru (min 6 karakter)"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  onTouchStart={() => setShowPassword(true)}
                  onTouchEnd={() => setShowPassword(false)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimal 6 karakter, harus kombinasi huruf dan angka
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordProfile(null);
              setNewPassword('');
              setShowPassword(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProfile} onOpenChange={(open) => !open && setDeleteProfile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the account for{' '}
              <strong>{deleteProfile?.email}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
